import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { getToken } from "../../utils/auth";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CONFIG from "../../config";

const AdminLogs = () => {
    const { t, i18n } = useTranslation();
    const [logs, setLogs] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const isArabic = i18n.language?.startsWith('ar');
    const [permissions, setPermissions] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);

    // Filters
    const [search, setSearch] = useState("");
    const [actionFilter, setActionFilter] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [distinctActions, setDistinctActions] = useState([]);

    // Modal
    const [selectedLog, setSelectedLog] = useState(null);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        setIsAdmin(!!user.isAdmin);
        setPermissions(user.permissions || []);
    }, []);

    const hasPerm = (p) => {
        if (!isAdmin) return false;
        if (permissions.includes('*') || permissions === '*') return true;
        return permissions.includes(p);
    };

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const token = getToken();
            const params = new URLSearchParams({
                page,
                per: 50,
                q: search,
                action: actionFilter,
                startDate,
                endDate
            });
            const res = await axios.get(`${CONFIG.API_URL}/api/admin/logs?${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLogs(res.data.data || []);
            setTotal(res.data.meta?.total || 0);
            if (res.data.meta?.distinctActions) {
                setDistinctActions(res.data.meta.distinctActions);
            }
        } catch (err) {
            console.error(err);
            if (err.response?.status === 403) {
                alert(t('admin.logs_page.access_denied'));
                navigate("/admin");
            }
        } finally { setLoading(false); }
    }, [page, search, actionFilter, startDate, endDate, navigate, t]);

    useEffect(() => {
        const timeout = setTimeout(fetchLogs, 500); // Debounce search
        return () => clearTimeout(timeout);
    }, [fetchLogs]);

    const handleClearLogs = async () => {
        if (!window.confirm(t('admin.logs_page.clear_confirm'))) return;
        try {
            const token = getToken();
            await axios.delete(`${CONFIG.API_URL}/api/admin/logs`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchLogs();
        } catch (err) {
            alert(t('admin.logs_page.clear_failed'));
        }
    };

    const renderValue = (value) => {
        if (typeof value !== 'string') return JSON.stringify(value);

        // Image Check
        if (value.match(/\.(jpeg|jpg|gif|png|webp)$/i) || (value.includes('cloudinary.com') && value.includes('/image/'))) {
            return (
                <div className="mt-1">
                    <img src={value} alt="Log Preview" className="h-20 w-auto rounded-lg border border-gray-200 object-cover" />
                    <div className="text-[10px] text-gray-400 mt-0.5 truncate max-w-xs">{value}</div>
                </div>
            );
        }

        // Video Check
        if (value.match(/\.(mp4|webm)$/i) || (value.includes('cloudinary.com') && value.includes('/video/'))) {
            return (
                <div className="mt-1">
                    <video src={value} controls className="h-24 w-auto rounded-lg border border-gray-200" />
                    <div className="text-[10px] text-gray-400 mt-0.5 truncate max-w-xs">{value}</div>
                </div>
            );
        }

        return value;
    };

    const formatDetails = (details) => {
        try {
            const parsed = JSON.parse(details);
            // Check if it's a deep audit "changes" object
            if (parsed.changes) {
                return (
                    <div className="space-y-4">
                        {Object.entries(parsed.changes).map(([field, change]) => (
                            <div key={field} className="bg-white p-3 rounded-lg border border-gray-100">
                                <div className="text-xs font-bold text-gray-400 uppercase mb-2">{field}</div>
                                {change.from !== undefined && change.to !== undefined ? (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <div className="text-[10px] text-red-400 font-bold mb-1">{t('admin.logs_page.before')}</div>
                                            <div className="text-sm font-mono text-gray-600 break-all">{renderValue(change.from)}</div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] text-green-500 font-bold mb-1">{t('admin.logs_page.after')}</div>
                                            <div className="text-sm font-mono text-gray-900 break-all">{renderValue(change.to)}</div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-sm font-mono break-all">{renderValue(change)}</div>
                                )}
                            </div>
                        ))}
                        {/* Render other fields in payload if any */}
                        {Object.entries(parsed).filter(([k]) => k !== 'changes').map(([key, value]) => (
                            <div key={key} className="pt-2 border-t border-gray-50 mt-2">
                                <span className="font-bold text-gray-500 text-xs uppercase">{key}:</span>{" "}
                                <span className="text-gray-600 font-mono text-sm">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                            </div>
                        ))}
                    </div>
                );
            }

            // Normal JSON fallback
            return (
                <div className="text-xs font-mono space-y-1">
                    {Object.entries(parsed).map(([key, value]) => (
                        <div key={key}>
                            <span className="font-bold text-gray-700">{key}:</span>{" "}
                            <span className="text-gray-600 break-all">
                                {renderValue(value)}
                            </span>
                        </div>
                    ))}
                </div>
            );
        } catch (e) {
            return details;
        }
    };

    return (
        <div className="max-w-7xl mx-auto my-12 p-8 bg-white rounded-[40px] shadow-2xl border border-gray-100 mt-24">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h2 className="text-3xl font-black text-gray-900 tracking-tight">{t('admin.logs_page.title')}</h2>
                    <p className="text-gray-500 mt-2 font-medium">{t('admin.logs_page.subtitle')}</p>
                </div>
                <div className="flex items-center gap-4">
                    <button onClick={handleClearLogs} className="bg-red-50 text-red-600 hover:bg-red-600 hover:text-white px-6 py-3 rounded-2xl font-bold text-xs uppercase tracking-widest transition-all">
                        {t('admin.logs_page.clear_btn')}
                    </button>
                    <button
                        onClick={() => navigate("/admin")}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all"
                    >
                        <span className={`inline-block transition-transform group-hover:-translate-x-1 ${isArabic ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
                        {t('admin.common.back', 'Back')}
                    </button>
                </div>
            </div>

            {/* Recent Processing / Objection Activities */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">Recent Processing & Objection Activities</h3>
                {logs.length === 0 ? (
                    <p className="text-sm text-blue-600">No recent activities.</p>
                ) : (
                    <ul className="space-y-2 max-h-48 overflow-y-auto">
                        {logs.filter(l => ['process', 'objection', 'restrict'].includes(l.action)).slice(0, 5).map((log) => (
                            <li key={log.id} className="text-sm text-blue-700">
                                <strong>{log.action.toUpperCase()}</strong> by {log.user?.username || 'system'} at {new Date(log.created_at).toLocaleString()}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8 bg-gray-50 p-6 rounded-3xl">
                <input
                    type="text"
                    placeholder={t('admin.logs_page.search_placeholder')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary outline-none"
                />
                <select
                    value={actionFilter}
                    onChange={(e) => setActionFilter(e.target.value)}
                    className="px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary outline-none"
                >
                    <option value="">{t('admin.logs_page.filter_all')}</option>
                    {distinctActions.map(action => (
                        <option key={action} value={action}>{action}</option>
                    ))}
                </select>
                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary outline-none"
                />
                <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary outline-none"
                />
            </div>

            {loading ? (
                <div className="text-center py-12"><i className="fas fa-spinner fa-spin text-3xl text-brand-primary"></i></div>
            ) : (
                <div className="overflow-x-auto min-h-[400px]">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className={isArabic
                                ? "text-right border-b border-gray-100"
                                : "text-left border-b border-gray-100"
                            }>
                                <th className="py-4 px-4 text-xs font-black uppercase text-gray-400">{t('admin.logs_page.table_date')}</th>
                                <th className="py-4 px-4 text-xs font-black uppercase text-gray-400">{t('admin.logs_page.table_user')}</th>
                                <th className="py-4 px-4 text-xs font-black uppercase text-gray-400">{t('admin.logs_page.table_action')}</th>
                                {hasPerm('logs.details') && <th className="py-4 px-4 text-xs font-black uppercase text-gray-400">{t('admin.logs_page.table_details', 'Details')}</th>}
                                {hasPerm('logs.ip') && <th className="py-4 px-4 text-xs font-black uppercase text-gray-400">{t('admin.logs_page.table_ip', 'IP')}</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {logs.map(log => (
                                <tr key={log.id} className={isArabic
                                    ? "text-right hover:bg-gray-50/50 transition-colors"
                                    : "text-left hover:bg-gray-50/50 transition-colors"
                                }>
                                    <td className="py-4 px-4 text-sm font-medium text-gray-500 whitespace-nowrap">
                                        {new Date(log.created_at).toLocaleString()}
                                    </td>
                                    <td className="py-4 px-4">
                                        {log.username ? (
                                            <div>
                                                <div className="font-bold text-gray-900 text-sm">{log.username}</div>
                                                <div className="text-xs text-gray-400">{log.email}</div>
                                            </div>
                                        ) : (
                                            <span className="text-gray-400 italic text-sm">{t('admin.logs_page.unknown_system')}</span>
                                        )}
                                    </td>
                                    <td className="py-4 px-4">
                                        <span className="bg-brand-primary/10 text-brand-primary px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider">
                                            {log.action}
                                        </span>
                                    </td>
                                    {hasPerm('logs.details') && (
                                        <td className="py-4 px-4 text-sm text-gray-600">
                                            <div className="max-w-xs">
                                                <div className="truncate text-xs font-mono mb-1">
                                                    {log.details}
                                                </div>
                                                <button
                                                    onClick={() => setSelectedLog(log)}
                                                    className="text-[10px] font-bold text-brand-primary uppercase hover:underline"
                                                >
                                                    {t('admin.logs_page.view_details')}
                                                </button>
                                            </div>
                                        </td>
                                    )}
                                    {hasPerm('logs.ip') && (
                                        <td className="py-4 px-4 text-xs text-gray-400 font-mono">
                                            {log.ip}
                                        </td>
                                    )}
                                </tr>
                            ))}
                            {logs.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="py-12 text-center text-gray-400 font-medium">{t('admin.logs_page.no_logs')}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {total > 50 && (
                <div className="flex justify-center gap-4 items-center mt-12">
                    <button onClick={() => setPage(s => Math.max(1, s - 1))} disabled={page <= 1} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50 transition-all font-bold">←</button>
                    <div className="text-xs font-black uppercase tracking-widest text-gray-400">{t('admin.common.page', { current: page, total: Math.ceil(total / 50) })}</div>
                    <button onClick={() => setPage(s => s + 1)} disabled={page >= Math.ceil(total / 50)} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50 transition-all font-bold">→</button>
                </div>
            )}

            {/* Details Modal */}
            {selectedLog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setSelectedLog(null)}>
                    <div className="bg-white rounded-3xl p-8 w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-2xl relative" onClick={e => e.stopPropagation()}>
                        <button
                            onClick={() => setSelectedLog(null)}
                            className={isArabic
                                ? "absolute top-6 left-6 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                                : "absolute top-6 right-6 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                            }>
                            ✕
                        </button>

                        <h3 className="text-2xl font-black text-gray-900 mb-6">{t('admin.logs_page.modal_title')}</h3>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-gray-50 p-4 rounded-xl">
                                    <div className="text-xs font-bold text-gray-400 uppercase mb-1">{t('admin.logs_page.modal_action')}</div>
                                    <div className="font-bold text-gray-900">{selectedLog.action}</div>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-xl">
                                    <div className="text-xs font-bold text-gray-400 uppercase mb-1">{t('admin.logs_page.modal_date')}</div>
                                    <div className="font-bold text-gray-900">{new Date(selectedLog.created_at).toLocaleString()}</div>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-xl">
                                    <div className="text-xs font-bold text-gray-400 uppercase mb-1">{t('admin.logs_page.modal_user')}</div>
                                    <div className="font-bold text-gray-900">{selectedLog.username || t('admin.logs_page.modal_system')}</div>
                                    <div className="text-xs text-gray-500">{selectedLog.email}</div>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-xl">
                                    <div className="text-xs font-bold text-gray-400 uppercase mb-1">{t('admin.logs_page.modal_ip')}</div>
                                    <div className="font-mono text-gray-600">{selectedLog.ip}</div>
                                </div>
                            </div>

                            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100">
                                <div className="text-xs font-bold text-gray-400 uppercase mb-4">{t('admin.logs_page.modal_payload')}</div>
                                {formatDetails(selectedLog.details)}
                            </div>
                        </div>

                        <div className="mt-8 flex justify-between">
                            <button
                                onClick={() => {
                                    const blob = new Blob([JSON.stringify(selectedLog, null, 2)], { type: "application/json" });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement("a");
                                    a.href = url;
                                    a.download = `log-${selectedLog.id}.json`;
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                    URL.revokeObjectURL(url);
                                }}
                                className="bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-bold hover:bg-gray-200 transition-colors"
                            >
                                <i className="fas fa-download mr-2"></i> {t('admin.common.download', 'Download')}
                            </button>
                            <button
                                onClick={() => setSelectedLog(null)}
                                className="bg-gray-900 text-white px-8 py-3 rounded-xl font-bold hover:bg-gray-800 transition-colors"
                            >
                                {t('admin.logs_page.close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminLogs;
